/**
 * Regole password (condivise da backend e frontend).
 * Almeno 12 caratteri, almeno tre tipi su quattro (minuscole, maiuscole, cifre, simboli), diversa dall'email.
 * Il controllo contro l'elenco delle password più comuni è solo lato server (backend/src/lib/common-passwords.ts).
 */
export const PASSWORD_MIN_LENGTH = 12;

export type PasswordIssue = "min_length" | "classes" | "email" | "common";

export const PASSWORD_MESSAGES: Record<"it" | "en", Record<PasswordIssue, string>> = {
  it: {
    min_length: `La password deve avere almeno ${PASSWORD_MIN_LENGTH} caratteri`,
    classes: "Usa almeno tre tipi di carattere su quattro: minuscole, maiuscole, cifre, simboli",
    email: "La password non può coincidere con l'email",
    common: "Questa password è troppo comune: scegline una meno prevedibile",
  },
  en: {
    min_length: `The password must be at least ${PASSWORD_MIN_LENGTH} characters long`,
    classes: "Use at least three of four character types: lowercase, uppercase, digits, symbols",
    email: "The password cannot be the same as the email",
    common: "This password is too common: choose a less predictable one",
  },
};

export function passwordClasses(pw: string): number {
  return [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].filter((r) => r.test(pw)).length;
}

/** Problemi della password (senza il controllo "common", che è lato server). */
export function passwordIssues(pw: string, email = ""): PasswordIssue[] {
  const out: PasswordIssue[] = [];
  if (pw.length < PASSWORD_MIN_LENGTH) out.push("min_length");
  if (passwordClasses(pw) < 3) out.push("classes");
  const e = email.trim().toLowerCase();
  if (e && (pw.toLowerCase() === e || (e.includes("@") && pw.toLowerCase() === e.split("@")[0]))) out.push("email");
  return out;
}

/** Robustezza 0-4 per l'indicatore nel form (solo orientativa: la regola vera è passwordIssues + elenco comuni). */
export function passwordStrength(pw: string, email = ""): 0 | 1 | 2 | 3 | 4 {
  if (!pw) return 0;
  const issues = passwordIssues(pw, email);
  if (issues.includes("email")) return 1;
  let s = 0;
  if (pw.length >= 8) s++;
  if (pw.length >= PASSWORD_MIN_LENGTH) s++;
  if (passwordClasses(pw) >= 3) s++;
  if (pw.length >= 16 && passwordClasses(pw) >= 3) s++;
  if (issues.length) s = Math.min(s, 2);
  return s as 0 | 1 | 2 | 3 | 4;
}
